import { useState, useEffect, useCallback } from 'react'
import {
  Clock, RefreshCw, CheckCircle, AlertTriangle, XCircle, Cloud,
} from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

function relativeTime(ts: string): string {
  if (!ts) return ''
  try { return formatDistanceToNow(new Date(ts), { addSuffix: false }) + ' ago' } catch { return ts }
}

function formatTime(ts: string): string {
  if (!ts) return ''
  try { return new Date(ts).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) } catch { return ts }
}

export default function OutageHistoryPage() {
  const [data, setData] = useState<any>(null)
  const [timeline, setTimeline] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [histRes, timelineRes] = await Promise.allSettled([
        api.get('/api/outages/history'),
        api.get('/api/outages/timeline', { params: { limit: 100 } }),
      ])
      if (histRes.status === 'fulfilled') setData(histRes.value.data)
      if (timelineRes.status === 'fulfilled') setTimeline(timelineRes.value.data.events || [])
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  const providers = data?.providers || []

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <Clock size={20} className="text-amber" /> Outage History
          </h1>
          <p className="text-[10px] text-text-secondary">
            Cloud provider status changes tracked from {data?.total_events || 0} events
          </p>
        </div>
        <button type="button" onClick={fetchData} className="btn-secondary text-xs">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {loading && !data && (
        <div className="flex items-center justify-center h-32">
          <RefreshCw size={20} className="animate-spin text-text-muted" />
        </div>
      )}

      {/* Provider incident counts */}
      {providers.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {providers.map((p: any) => (
            <div key={p.provider} className={clsx('card p-3', p.total_incidents > 0 ? 'border border-amber/20' : '')}>
              <div className="flex items-center gap-2 mb-1">
                <Cloud size={14} className={p.total_incidents > 0 ? 'text-amber' : 'text-emerald'} />
                <span className="text-xs font-semibold text-text-primary">{p.provider}</span>
              </div>
              <div className="flex items-center gap-3 text-[10px]">
                <span className="text-rose">{p.total_incidents} incidents</span>
                <span className="text-emerald">{p.total_recoveries} recoveries</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Timeline */}
      <div className="card p-4">
        <h2 className="text-xs font-semibold text-text-primary mb-3">Status Change Timeline</h2>
        {timeline.length === 0 && !loading && (
          <div className="text-center py-8">
            <CheckCircle size={32} className="mx-auto mb-2 text-emerald opacity-50" />
            <p className="text-xs text-text-muted">No status changes recorded yet.</p>
            <p className="text-[10px] text-text-muted">Cloud status is checked every 15 minutes. Changes will appear here.</p>
          </div>
        )}
        <div className="space-y-1 max-h-[500px] overflow-y-auto">
          {timeline.map((event, i) => {
            const isRecovery = event.type === 'recovered'
            return (
              <div key={i} className="flex items-start gap-3 py-2 border-b border-border last:border-0">
                <div className="flex flex-col items-center shrink-0">
                  {isRecovery ? (
                    <CheckCircle size={16} className="text-emerald" />
                  ) : event.status === 'outage' ? (
                    <XCircle size={16} className="text-rose" />
                  ) : (
                    <AlertTriangle size={16} className="text-amber" />
                  )}
                  <div className="w-px h-full bg-border mt-1" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-semibold text-text-primary">{event.provider}</span>
                    <span className={clsx('badge text-[9px]',
                      isRecovery ? 'badge-green' : event.status === 'outage' ? 'badge-red' : 'badge-amber')}>
                      {event.previous_status} → {event.status}
                    </span>
                  </div>
                  <span className="text-[10px] text-text-muted">{formatTime(event.timestamp)} ({relativeTime(event.timestamp)})</span>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
